import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import {
  useProspectDetail,
  useUpdateProspect,
  useDeleteProspect,
} from '@/hooks/useProspection'
import {
  STATUT_LABELS,
  STATUT_COLORS,
  EMAIL_STATUS_LABELS,
  EMAIL_STATUS_COLORS,
} from '@/types/prospection'
import type { ProspectStatut } from '@/types/prospection'
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  Globe,
  XCircle,
  MapPin,
  Pencil,
  Trash2,
  Save,
  X,
  TrendingUp,
  FileText,
  Briefcase,
  Calendar,
  Users,
  DollarSign,
  BarChart3,
  Image,
  ExternalLink,
  Zap,
  Target,
  Shield,
  Lightbulb,
  AlertTriangle,
  Crosshair,
} from 'lucide-react'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import SendProspectEmailModal from '@/components/admin/prospection/SendProspectEmailModal'

export default function ProspectionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: prospect, isLoading, error } = useProspectDetail(id)
  const updateMutation = useUpdateProspect()
  const deleteMutation = useDeleteProspect()
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<Record<string, any>>({})
  const [showEmailModal, setShowEmailModal] = useState(false)

  const startEdit = () => {
    if (!prospect) return
    setFormData({ ...prospect })
    setIsEditing(true)
  }

  const cancelEdit = () => {
    setIsEditing(false)
    setFormData({})
  }

  const handleSave = async () => {
    if (!id) return
    const { id: _id, created_at, updated_at, ...payload } = formData
    await updateMutation.mutateAsync({ id, ...payload })
    setIsEditing(false)
  }

  const handleDelete = async () => {
    if (!id || !confirm('Supprimer ce prospect ?')) return
    await deleteMutation.mutateAsync(id)
    navigate('/prospection')
  }

  const handleStatutChange = async (newStatut: ProspectStatut) => {
    if (!id) return
    await updateMutation.mutateAsync({ id, statut: newStatut })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !prospect) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-brown-900 mb-2">
          Prospect non trouve
        </h2>
        <Link to="/prospection">
          <Button variant="outline">Retour a la liste</Button>
        </Link>
      </div>
    )
  }

  const formatDate = (date: string | null) => {
    if (!date) return '-'
    return format(new Date(date), 'd MMMM yyyy', { locale: fr })
  }

  const formatDateTime = (date: string | null) => {
    if (!date) return '-'
    return format(new Date(date), "d MMM yyyy 'a' HH:mm", { locale: fr })
  }

  const formatCurrency = (amount: number | null) => {
    if (amount == null) return '-'
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount)
  }

  const displayName = prospect.nom_commercial || prospect.raison_sociale || 'Sans nom'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/prospection"
            className="p-2 rounded-lg hover:bg-beige-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-brown-600" />
          </Link>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-display font-bold text-brown-900">
                {displayName}
              </h1>
              <Badge variant={STATUT_COLORS[prospect.statut || 'lead']} size="md">
                {STATUT_LABELS[prospect.statut || 'lead']}
              </Badge>
              {prospect.a_un_site ? (
                <Badge variant="mint" size="sm">
                  <Globe className="w-3 h-3 mr-1" /> Site web
                </Badge>
              ) : (
                <Badge variant="red" size="sm">
                  <XCircle className="w-3 h-3 mr-1" /> Pas de site
                </Badge>
              )}
            </div>
            <p className="mt-1 text-brown-600">
              {[prospect.secteur, prospect.ville].filter(Boolean).join(' - ')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              {prospect.email && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowEmailModal(true)}
                  leftIcon={<Mail className="w-4 h-4" />}
                >
                  Envoyer un email
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={startEdit}
                leftIcon={<Pencil className="w-4 h-4" />}
              >
                Modifier
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleDelete}
                isLoading={deleteMutation.isPending}
                leftIcon={<Trash2 className="w-4 h-4" />}
              >
                Supprimer
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                isLoading={updateMutation.isPending}
                leftIcon={<Save className="w-4 h-4" />}
              >
                Enregistrer
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={cancelEdit}
                leftIcon={<X className="w-4 h-4" />}
              >
                Annuler
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Quick status change */}
      {!isEditing && (
        <Card padding="sm">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-brown-600">Statut :</span>
            {(Object.entries(STATUT_LABELS) as [ProspectStatut, string][]).map(
              ([value, label]) => (
                <button
                  key={value}
                  onClick={() => handleStatutChange(value)}
                  disabled={value === (prospect.statut || 'lead')}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                    value === (prospect.statut || 'lead')
                      ? 'bg-mint-100 text-mint-800 cursor-default'
                      : 'bg-beige-100 text-brown-600 hover:bg-beige-200'
                  }`}
                >
                  {label}
                </button>
              )
            )}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne principale */}
        <div className="lg:col-span-2 space-y-6">
          {/* Infos entreprise */}
          <Card>
            <h3 className="text-lg font-semibold text-brown-900 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-mint-600" />
              Entreprise
            </h3>
            {isEditing ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Raison sociale" value={formData.raison_sociale || ''} onChange={(e) => setFormData({ ...formData, raison_sociale: e.target.value })} />
                <Input label="Nom commercial" value={formData.nom_commercial || ''} onChange={(e) => setFormData({ ...formData, nom_commercial: e.target.value })} />
                <Input label="SIREN" value={formData.siren || ''} onChange={(e) => setFormData({ ...formData, siren: e.target.value })} />
                <Input label="SIRET" value={formData.siret || ''} onChange={(e) => setFormData({ ...formData, siret: e.target.value })} />
                <Input label="Forme juridique" value={formData.forme_juridique || ''} onChange={(e) => setFormData({ ...formData, forme_juridique: e.target.value })} />
                <Input label="Secteur" value={formData.secteur || ''} onChange={(e) => setFormData({ ...formData, secteur: e.target.value })} />
                <Input label="Code NAF" value={formData.naf || ''} onChange={(e) => setFormData({ ...formData, naf: e.target.value })} />
                <Input label="Dirigeant" value={formData.dirigeant || ''} onChange={(e) => setFormData({ ...formData, dirigeant: e.target.value })} />
                <Input label="Date creation" type="date" value={formData.date_creation_entreprise || ''} onChange={(e) => setFormData({ ...formData, date_creation_entreprise: e.target.value || null })} />
                <Input label="Nb etablissements" type="number" value={formData.nb_etablissements || ''} onChange={(e) => setFormData({ ...formData, nb_etablissements: e.target.value ? Number(e.target.value) : null })} />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoRow icon={<Building2 className="w-4 h-4" />} label="Raison sociale" value={prospect.raison_sociale} />
                <InfoRow icon={<Briefcase className="w-4 h-4" />} label="Nom commercial" value={prospect.nom_commercial} />
                <InfoRow icon={<FileText className="w-4 h-4" />} label="SIREN / SIRET" value={[prospect.siren, prospect.siret].filter(Boolean).join(' / ') || null} />
                <InfoRow icon={<FileText className="w-4 h-4" />} label="Forme juridique" value={prospect.forme_juridique} />
                <InfoRow icon={<BarChart3 className="w-4 h-4" />} label="Secteur" value={prospect.secteur} />
                <InfoRow icon={<FileText className="w-4 h-4" />} label="Code NAF" value={prospect.naf} />
                <InfoRow icon={<Users className="w-4 h-4" />} label="Dirigeant" value={prospect.dirigeant} />
                <InfoRow icon={<Calendar className="w-4 h-4" />} label="Date creation" value={formatDate(prospect.date_creation_entreprise)} />
                <InfoRow icon={<Building2 className="w-4 h-4" />} label="Nb etablissements" value={prospect.nb_etablissements?.toString() || null} />
                <InfoRow icon={<FileText className="w-4 h-4" />} label="Etat administratif" value={prospect.etat_administratif === 'A' ? 'Actif' : prospect.etat_administratif} />
              </div>
            )}
          </Card>

          {/* Contact & Localisation */}
          <Card>
            <h3 className="text-lg font-semibold text-brown-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-mint-600" />
              Contact & Localisation
            </h3>
            {isEditing ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Email" type="email" value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                <Input label="Telephone" value={formData.telephone || ''} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })} />
                <Input label="Site web" value={formData.site_web || ''} onChange={(e) => setFormData({ ...formData, site_web: e.target.value })} />
                <Input label="Adresse" value={formData.adresse || ''} onChange={(e) => setFormData({ ...formData, adresse: e.target.value })} />
                <Input label="Code postal" value={formData.code_postal || ''} onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })} />
                <Input label="Ville" value={formData.ville || ''} onChange={(e) => setFormData({ ...formData, ville: e.target.value })} />
                <Input label="Departement" value={formData.departement || ''} onChange={(e) => setFormData({ ...formData, departement: e.target.value })} />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoRow icon={<Mail className="w-4 h-4" />} label="Email" value={prospect.email} href={prospect.email ? `mailto:${prospect.email}` : undefined} />
                <InfoRow icon={<Phone className="w-4 h-4" />} label="Telephone" value={prospect.telephone} href={prospect.telephone ? `tel:${prospect.telephone}` : undefined} />
                <InfoRow icon={<Globe className="w-4 h-4" />} label="Site web" value={prospect.site_web} href={prospect.site_web || undefined} external />
                <InfoRow icon={<MapPin className="w-4 h-4" />} label="Adresse" value={prospect.adresse} />
                <InfoRow icon={<MapPin className="w-4 h-4" />} label="Code postal / Ville" value={[prospect.code_postal, prospect.ville].filter(Boolean).join(' ') || null} />
                <InfoRow icon={<MapPin className="w-4 h-4" />} label="Departement" value={prospect.departement} />
                <InfoRow icon={<Globe className="w-4 h-4" />} label="Technologie site" value={prospect.technologie_site} />
              </div>
            )}
          </Card>

          {/* Donnees financieres */}
          <Card>
            <h3 className="text-lg font-semibold text-brown-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-mint-600" />
              Donnees financieres
            </h3>
            {isEditing ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Effectifs" value={formData.effectifs || ''} onChange={(e) => setFormData({ ...formData, effectifs: e.target.value })} />
                <Input label="Capital" type="number" value={formData.capital || ''} onChange={(e) => setFormData({ ...formData, capital: e.target.value ? Number(e.target.value) : null })} />
                <Input label="CA" type="number" value={formData.ca || ''} onChange={(e) => setFormData({ ...formData, ca: e.target.value ? Number(e.target.value) : null })} />
                <Input label="Annee CA" type="number" value={formData.ca_annee || ''} onChange={(e) => setFormData({ ...formData, ca_annee: e.target.value ? Number(e.target.value) : null })} />
                <Input label="Resultat net" type="number" value={formData.resultat_net || ''} onChange={(e) => setFormData({ ...formData, resultat_net: e.target.value ? Number(e.target.value) : null })} />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoRow icon={<Users className="w-4 h-4" />} label="Effectifs" value={prospect.effectifs} />
                <InfoRow icon={<DollarSign className="w-4 h-4" />} label="Capital" value={formatCurrency(prospect.capital)} />
                <InfoRow icon={<TrendingUp className="w-4 h-4" />} label="Chiffre d'affaires" value={prospect.ca ? `${formatCurrency(prospect.ca)} (${prospect.ca_annee || '?'})` : '-'} />
                <InfoRow icon={<BarChart3 className="w-4 h-4" />} label="Resultat net" value={formatCurrency(prospect.resultat_net)} />
              </div>
            )}
          </Card>

          {/* Notes */}
          <Card>
            <h3 className="text-lg font-semibold text-brown-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-mint-600" />
              Notes
            </h3>
            {isEditing ? (
              <textarea
                className="w-full px-4 py-2 border border-beige-300 rounded-lg text-brown-900 focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent min-h-[120px]"
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notes de suivi..."
              />
            ) : (
              <p className="text-brown-700 whitespace-pre-wrap">
                {prospect.notes || 'Aucune note'}
              </p>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Scoring */}
          <Card>
            <h3 className="text-lg font-semibold text-brown-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-mint-600" />
              Scoring
            </h3>
            <dl className="space-y-3">
              <ScoreRow label="Priorite" value={prospect.score_priorite} />
              <ScoreRow label="Besoin" value={prospect.score_besoin} />
              <ScoreRow label="Capacite" value={prospect.score_capacite} />
              <ScoreRow label="Timing" value={prospect.score_timing} />
            </dl>
          </Card>

          {/* Email */}
          <Card>
            <h3 className="text-lg font-semibold text-brown-900 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-mint-600" />
              Email de prospection
            </h3>
            <dl className="space-y-3">
              <DetailItem label="Statut">
                <Badge variant={EMAIL_STATUS_COLORS[prospect.email_status || 'pending']}>
                  {EMAIL_STATUS_LABELS[prospect.email_status || 'pending']}
                </Badge>
              </DetailItem>
              {prospect.email_subject && (
                <DetailItem label="Sujet">
                  <span className="text-xs">{prospect.email_subject}</span>
                </DetailItem>
              )}
              {prospect.email_sent_at && (
                <DetailItem label="Envoye le">
                  {formatDateTime(prospect.email_sent_at)}
                </DetailItem>
              )}
            </dl>
            {prospect.email_body && (
              <div className="mt-4 p-3 bg-beige-50 rounded-lg">
                <p className="text-xs text-brown-500 mb-1 font-medium">Corps du mail :</p>
                <p className="text-sm text-brown-700 whitespace-pre-wrap">{prospect.email_body}</p>
              </div>
            )}
          </Card>

          {/* Mockup */}
          {(prospect.mockup_html_path || prospect.mockup_screenshot_path || prospect.mockup_pdf_path) && (
            <Card>
              <h3 className="text-lg font-semibold text-brown-900 mb-4 flex items-center gap-2">
                <Image className="w-5 h-5 text-mint-600" />
                Mockup
              </h3>
              <div className="space-y-2">
                {prospect.mockup_screenshot_path && (
                  <a
                    href={prospect.mockup_screenshot_path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-mint-600 hover:text-mint-700"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Screenshot
                  </a>
                )}
                {prospect.mockup_pdf_path && (
                  <a
                    href={prospect.mockup_pdf_path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-mint-600 hover:text-mint-700"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    PDF
                  </a>
                )}
                {prospect.mockup_html_path && (
                  <a
                    href={prospect.mockup_html_path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-mint-600 hover:text-mint-700"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    HTML
                  </a>
                )}
              </div>
            </Card>
          )}

          {/* Audit JSON */}
          {prospect.audit && Object.keys(prospect.audit).length > 0 && (
            <Card>
              <h3 className="text-lg font-semibold text-brown-900 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-mint-600" />
                Audit
              </h3>
              <pre className="text-xs text-brown-600 bg-beige-50 p-3 rounded-lg overflow-auto max-h-[300px]">
                {JSON.stringify(prospect.audit, null, 2)}
              </pre>
            </Card>
          )}

          {/* Metadata */}
          <Card>
            <h3 className="text-sm font-medium text-brown-500 mb-3">Metadata</h3>
            <dl className="space-y-2 text-sm">
              <DetailItem label="Source">{prospect.source || '-'}</DetailItem>
              <DetailItem label="Collecte le">{formatDateTime(prospect.date_collecte)}</DetailItem>
              <DetailItem label="Cree le">{formatDateTime(prospect.created_at)}</DetailItem>
              <DetailItem label="Modifie le">{formatDateTime(prospect.updated_at)}</DetailItem>
              {prospect.tags && prospect.tags.length > 0 && (
                <DetailItem label="Tags">
                  <div className="flex flex-wrap gap-1">
                    {prospect.tags.map((tag) => (
                      <Badge key={tag} variant="beige" size="sm">{tag}</Badge>
                    ))}
                  </div>
                </DetailItem>
              )}
            </dl>
          </Card>
        </div>
      </div>

      {/* Qualification - Full width */}
      {prospect.qualification && (
        <QualificationSection qualification={prospect.qualification} />
      )}

      {/* Email Modal */}
      {showEmailModal && prospect && (
        <SendProspectEmailModal
          prospect={prospect}
          onClose={() => setShowEmailModal(false)}
        />
      )}
    </div>
  )
}

function InfoRow({
  icon,
  label,
  value,
  href,
  external,
}: {
  icon: React.ReactNode
  label: string
  value: string | null
  href?: string
  external?: boolean
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-brown-400 mt-0.5">{icon}</span>
      <div>
        <p className="text-xs text-brown-500">{label}</p>
        {href && value ? (
          <a
            href={href}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
            className="text-sm text-mint-600 hover:text-mint-700 underline"
          >
            {value}
          </a>
        ) : (
          <p className="text-sm text-brown-900">{value || '-'}</p>
        )}
      </div>
    </div>
  )
}

function DetailItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start gap-2">
      <dt className="text-sm text-brown-500">{label}</dt>
      <dd className="text-sm text-brown-900 text-right">{children}</dd>
    </div>
  )
}

function ScoreRow({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-sm text-brown-500">{label}</span>
      {value != null ? (
        <div className="flex items-center gap-2">
          <div className="w-24 h-2 bg-beige-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-mint-500 rounded-full"
              style={{ width: `${Math.min(value, 100)}%` }}
            />
          </div>
          <span className="text-sm font-medium text-brown-900 w-8 text-right">{value}</span>
        </div>
      ) : (
        <span className="text-sm text-brown-400">-</span>
      )}
    </div>
  )
}

function safeString(val: any): string {
  if (val == null) return '-'
  if (typeof val === 'string') return val
  if (typeof val === 'number' || typeof val === 'boolean') return String(val)
  return JSON.stringify(val)
}

function RenderValue({ value }: { value: any }) {
  if (value == null) return <span className="text-brown-400">-</span>
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return <>{String(value)}</>
  }
  if (Array.isArray(value)) {
    return (
      <ul className="space-y-1">
        {value.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-brown-700">
            <span className="w-1.5 h-1.5 rounded-full bg-mint-400 mt-1.5 flex-shrink-0" />
            <RenderValue value={item} />
          </li>
        ))}
      </ul>
    )
  }
  if (typeof value === 'object') {
    return (
      <dl className="space-y-1.5 text-sm">
        {Object.entries(value).map(([k, v]) => (
          <div key={k} className="flex flex-col">
            <dt className="text-xs font-medium text-brown-500 capitalize">{k.replace(/_/g, ' ')}</dt>
            <dd className="text-brown-800"><RenderValue value={v} /></dd>
          </div>
        ))}
      </dl>
    )
  }
  return <>{String(value)}</>
}

function QualificationSection({ qualification }: { qualification: Record<string, any> }) {
  const q = qualification

  const urgenceColors: Record<string, string> = {
    FORT: 'bg-red-100 text-red-800 border-red-200',
    ELEVE: 'bg-orange-100 text-orange-800 border-orange-200',
    MOYEN: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    FAIBLE: 'bg-gray-100 text-gray-700 border-gray-200',
    SUSPENDU: 'bg-purple-100 text-purple-800 border-purple-200',
  }

  const prioriteColors: Record<string, string> = {
    P0: 'bg-red-100 text-red-700',
    P1: 'bg-orange-100 text-orange-700',
    P2: 'bg-yellow-100 text-yellow-700',
    P3: 'bg-gray-100 text-gray-700',
  }

  const confianceColors: Record<string, string> = {
    HAUTE: 'text-green-700 bg-green-50',
    MOYENNE: 'text-yellow-700 bg-yellow-50',
    BASSE: 'text-red-700 bg-red-50',
  }

  return (
    <div className="space-y-6">
      {/* Alerte */}
      {q.alerte && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-800">Alerte</p>
            <p className="text-sm text-red-700">{safeString(q.alerte)}</p>
          </div>
        </div>
      )}

      {/* Urgence + Strategie + Meta */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-brown-900 flex items-center gap-2">
              <Zap className="w-5 h-5 text-mint-600" />
              Qualification
            </h3>
            {q.urgence && (
              <span className={`px-3 py-1 text-sm font-bold rounded-full border ${urgenceColors[q.urgence] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                {q.urgence}
              </span>
            )}
          </div>
          <dl className="space-y-3">
            {q.confiance_donnees && (
              <div>
                <dt className="text-xs font-medium text-brown-500 uppercase tracking-wider mb-1">Confiance donnees</dt>
                <dd>
                  {typeof q.confiance_donnees === 'string' ? (
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${confianceColors[q.confiance_donnees] || 'text-brown-700 bg-beige-50'}`}>
                      {q.confiance_donnees}
                    </span>
                  ) : typeof q.confiance_donnees === 'object' ? (
                    <div className="mt-1 space-y-1">
                      {q.confiance_donnees.niveau && (
                        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${confianceColors[q.confiance_donnees.niveau] || 'text-brown-700 bg-beige-50'}`}>
                          {q.confiance_donnees.niveau}
                        </span>
                      )}
                      <div className="text-sm"><RenderValue value={q.confiance_donnees} /></div>
                    </div>
                  ) : (
                    <span className="text-sm text-brown-900">{String(q.confiance_donnees)}</span>
                  )}
                </dd>
              </div>
            )}
            {q.date_qualification && (
              <div>
                <dt className="text-xs font-medium text-brown-500 uppercase tracking-wider mb-1">Date de qualification</dt>
                <dd className="text-sm text-brown-900">{q.date_qualification}</dd>
              </div>
            )}
            {q.nom_commercial_identifie && (
              <div>
                <dt className="text-xs font-medium text-brown-500 uppercase tracking-wider mb-1">Nom commercial identifie</dt>
                <dd className="text-sm text-brown-900 font-medium">{q.nom_commercial_identifie}</dd>
              </div>
            )}
            {q.telephone_trouve && (
              <div>
                <dt className="text-xs font-medium text-brown-500 uppercase tracking-wider mb-1">Telephone trouve</dt>
                <dd>
                  <a href={`tel:${q.telephone_trouve}`} className="text-sm text-mint-600 hover:text-mint-700 font-medium">
                    {q.telephone_trouve}
                  </a>
                </dd>
              </div>
            )}
          </dl>
        </Card>

        {q.strategie && (
          <Card>
            <h3 className="text-lg font-semibold text-brown-900 mb-4 flex items-center gap-2">
              <Crosshair className="w-5 h-5 text-mint-600" />
              Strategie d'approche
            </h3>
            <dl className="space-y-3">
              {q.strategie.canal && (
                <div>
                  <dt className="text-xs font-medium text-brown-500 uppercase tracking-wider mb-1">Canal</dt>
                  <dd className="text-sm text-brown-900">{safeString(q.strategie.canal)}</dd>
                </div>
              )}
              {q.strategie.timing && (
                <div>
                  <dt className="text-xs font-medium text-brown-500 uppercase tracking-wider mb-1">Timing</dt>
                  <dd className="text-sm text-brown-900">{safeString(q.strategie.timing)}</dd>
                </div>
              )}
              {q.strategie.message_cle && (
                <div>
                  <dt className="text-xs font-medium text-brown-500 uppercase tracking-wider mb-1">Message cle</dt>
                  <dd className="text-sm text-brown-900 italic bg-beige-50 p-2 rounded-lg">"{safeString(q.strategie.message_cle)}"</dd>
                </div>
              )}
              {/* Render any extra strategie keys */}
              {Object.entries(q.strategie as Record<string, any>)
                .filter(([k]) => !['canal', 'timing', 'message_cle'].includes(k))
                .map(([key, val]) => (
                  <div key={key}>
                    <dt className="text-xs font-medium text-brown-500 uppercase tracking-wider mb-1">{key.replace(/_/g, ' ')}</dt>
                    <dd className="text-sm text-brown-900"><RenderValue value={val} /></dd>
                  </div>
                ))}
            </dl>
          </Card>
        )}
      </div>

      {/* Argumentaire */}
      {q.argumentaire && (
        <Card>
          <h3 className="text-lg font-semibold text-brown-900 mb-4 flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-mint-600" />
            Argumentaire commercial
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {q.argumentaire.probleme && (
              <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                <h4 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" />
                  Probleme
                </h4>
                <p className="text-sm text-red-700">{safeString(q.argumentaire.probleme)}</p>
              </div>
            )}
            {q.argumentaire.solution && (
              <div className="p-4 bg-mint-50 rounded-lg border border-mint-100">
                <h4 className="text-sm font-semibold text-mint-800 mb-2 flex items-center gap-1.5">
                  <Shield className="w-4 h-4" />
                  Solution
                </h4>
                <p className="text-sm text-mint-700">{safeString(q.argumentaire.solution)}</p>
              </div>
            )}
            {q.argumentaire.impact && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-1.5">
                  <TrendingUp className="w-4 h-4" />
                  Impact
                </h4>
                <p className="text-sm text-blue-700">{safeString(q.argumentaire.impact)}</p>
              </div>
            )}
          </div>
          {/* Extra argumentaire keys */}
          {Object.entries(q.argumentaire as Record<string, any>)
            .filter(([k]) => !['probleme', 'solution', 'impact'].includes(k))
            .length > 0 && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(q.argumentaire as Record<string, any>)
                .filter(([k]) => !['probleme', 'solution', 'impact'].includes(k))
                .map(([key, val]) => (
                  <div key={key} className="p-3 bg-beige-50 rounded-lg">
                    <h4 className="text-xs font-semibold text-brown-600 uppercase mb-1">{key.replace(/_/g, ' ')}</h4>
                    <div className="text-sm text-brown-700"><RenderValue value={val} /></div>
                  </div>
                ))}
            </div>
          )}
        </Card>
      )}

      {/* Challenges + Concurrents */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Array.isArray(q.challenges) && q.challenges.length > 0 && (
          <Card>
            <h3 className="text-lg font-semibold text-brown-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-mint-600" />
              Challenges identifies
              <span className="text-xs font-normal text-brown-400 ml-1">({q.challenges.length})</span>
            </h3>
            <ul className="space-y-2">
              {q.challenges.map((c: any, i: number) => (
                <li key={i} className="flex items-start gap-2 text-sm text-brown-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />
                  <span><RenderValue value={typeof c === 'string' ? c : c.description || c.probleme || c} /></span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {Array.isArray(q.concurrents) && q.concurrents.length > 0 && (
          <Card>
            <h3 className="text-lg font-semibold text-brown-900 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-mint-600" />
              Concurrents
              <span className="text-xs font-normal text-brown-400 ml-1">({q.concurrents.length})</span>
            </h3>
            <ul className="space-y-2">
              {q.concurrents.map((c: any, i: number) => (
                <li key={i} className="flex items-start gap-2 text-sm text-brown-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-1.5 flex-shrink-0" />
                  <span><RenderValue value={typeof c === 'string' ? c : c.nom || c.name || c} /></span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>

      {/* Axes d'amelioration */}
      {Array.isArray(q.axes_amelioration) && q.axes_amelioration.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-brown-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-mint-600" />
            Axes d'amelioration
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-beige-200">
                  <th className="text-left px-4 py-2 text-xs font-semibold text-brown-500 uppercase">Priorite</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-brown-500 uppercase">Axe</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-brown-500 uppercase">Offre</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-brown-500 uppercase">Estimation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-beige-100">
                {q.axes_amelioration.map((axe: any, i: number) => (
                  <tr key={i}>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${prioriteColors[axe.priorite] || 'bg-gray-100 text-gray-700'}`}>
                        {axe.priorite}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-brown-900 font-medium">{axe.axe || axe.action || '-'}</td>
                    <td className="px-4 py-3 text-sm text-brown-600">{axe.offre || '-'}</td>
                    <td className="px-4 py-3 text-sm text-brown-900 font-medium text-right">{axe.estimation || axe.impact_attendu || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Catch-all: render any remaining keys not handled above */}
      {Object.entries(q)
        .filter(([k]) => !['urgence', 'strategie', 'argumentaire', 'challenges', 'concurrents', 'axes_amelioration', 'confiance_donnees', 'date_qualification', 'alerte', 'nom_commercial_identifie', 'telephone_trouve'].includes(k))
        .map(([key, val]) => (
          <Card key={key}>
            <h3 className="text-lg font-semibold text-brown-900 mb-4 capitalize">
              {key.replace(/_/g, ' ')}
            </h3>
            <div className="text-sm text-brown-700">
              <RenderValue value={val} />
            </div>
          </Card>
        ))}
    </div>
  )
}
